import * as React from 'react';
import { unstable_useId as useId } from '@mui/material';
import {
  GridApiPro,
  gridColumnLookupSelector,
  GridLoadIcon,
  GridFilterInputValueProps,
  GridTypeFilterInputValueProps,
  useGridSelector,
  ControllerSelect,
  ControllerSelectProps,
  ControllerTextField,
  ControllerTextFieldProps,
  MdClear,
  ControllerAutocomplete,
  AutocompleteOption,
} from '../..';

const SUBMIT_FILTER_STROKE_TIME = 500;
type TimeoutID = ReturnType<typeof setTimeout>;

export function ControllerGridFilterInputBoolean(props: GridFilterInputValueProps & ControllerSelectProps) {
  const { item, applyValue, apiRef: _apiRef, ...others } = props;
  const apiRef = _apiRef as React.MutableRefObject<GridApiPro>;
  const [filterValueState, setFilterValueState] = React.useState(item.value || '');

  const onFilterChange = React.useCallback(
    (event) => {
      const value = event.target.value;
      setFilterValueState(value);
      applyValue({ ...item, value });
    },
    [applyValue, item]
  );

  React.useEffect(() => {
    setFilterValueState(item.value || '');
  }, [item.value]);

  return (
    <ControllerSelect
      label={apiRef.current.getLocaleText('filterPanelInputLabel')}
      value={filterValueState}
      onChange={onFilterChange}
      SelectProps={{ native: true }}
      {...others}
    >
      <option value=''>{apiRef.current.getLocaleText('filterValueAny')}</option>
      <option value='true'>{apiRef.current.getLocaleText('filterValueTrue')}</option>
      <option value='false'>{apiRef.current.getLocaleText('filterValueFalse')}</option>
    </ControllerSelect>
  );
}

export function ControllerGridFilterInputValue(
  props: GridTypeFilterInputValueProps & ControllerTextFieldProps
) {
  const { item, applyValue, apiRef: _apiRef, type, children, ...others } = props;
  const apiRef = _apiRef as React.MutableRefObject<GridApiPro>;
  const columnsLookup = useGridSelector(apiRef, gridColumnLookupSelector);
  const filterTimeout = React.useRef<TimeoutID | undefined>();
  const [filterValueState, setFilterValueState] = React.useState(item.value || '');
  const [applying, setIsApplying] = React.useState(false);
  const id = useId();

  const valueOptions = React.useMemo(() => {
    const colDef = columnsLookup[item.columnField];

    if (colDef?.type === 'singleSelect') {
      return typeof colDef.valueOptions === 'function'
        ? colDef.valueOptions({ field: colDef.field })
        : colDef.valueOptions;
    }
  }, [columnsLookup, item.columnField]);

  const handleClearSearch = React.useCallback(() => {
    apiRef.current.deleteFilterItem(item);
    setFilterValueState('');
  }, [apiRef, item]);

  const InputProps = applying
    ? { endAdornment: <GridLoadIcon /> }
    : filterValueState
    ? { endAdornment: <MdClear onClick={handleClearSearch} /> }
    : others.InputProps;

  const onDebouncedFilterChange = React.useCallback(
    (event) => {
      clearTimeout(filterTimeout.current as TimeoutID);
      const value = event.target.value;
      setIsApplying(true);
      if (value) {
        setFilterValueState(value);
      } else {
        handleClearSearch();
      }
      filterTimeout.current = setTimeout(() => {
        applyValue({ ...item, value: value?.trim() });
        setIsApplying(false);
      }, SUBMIT_FILTER_STROKE_TIME);
    },
    [applyValue, handleClearSearch, item]
  );

  const onFilterChange = React.useCallback(
    (event) => {
      const value = event.target.value;
      setFilterValueState(value);
      applyValue({ ...item, value: value?.trim() });
    },
    [applyValue, item]
  );

  React.useEffect(() => {
    return () => {
      clearTimeout(filterTimeout.current as TimeoutID);
    };
  }, []);

  React.useEffect(() => {
    setFilterValueState(item.value || '');
  }, [item.value]);

  return type === 'singleSelect' ? (
    <ControllerSelect
      id={id}
      label={apiRef.current.getLocaleText('filterPanelInputLabel')}
      placeholder={apiRef.current.getLocaleText('filterPanelInputPlaceholder')}
      value={filterValueState}
      onChange={onFilterChange}
      SelectProps={{ native: true }}
      {...others}
    >
      {valueOptions?.map((option) =>
        typeof option === 'string' || typeof option === 'number' ? (
          <option key={option} value={option}>
            {option}
          </option>
        ) : (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        )
      )}
    </ControllerSelect>
  ) : (
    <ControllerTextField
      id={id}
      label={apiRef.current.getLocaleText('filterPanelInputLabel')}
      placeholder={apiRef.current.getLocaleText('filterPanelInputPlaceholder')}
      value={filterValueState}
      onChange={onDebouncedFilterChange}
      InputProps={InputProps}
      {...others}
    />
  );
}

export function ControllerGridFilterInputSingleSelect(
  props: GridTypeFilterInputValueProps & ControllerSelectProps
) {
  const { item, applyValue, apiRef: _apiRef, type, children, ...others } = props;
  const apiRef = _apiRef as React.MutableRefObject<GridApiPro>;
  const columnsLookup = useGridSelector(apiRef, gridColumnLookupSelector);
  const [filterValueState, setFilterValueState] = React.useState(item.value || '');
  const id = useId();

  const valueOptions = React.useMemo(() => {
    const colDef = columnsLookup[item.columnField];

    if (colDef?.type === 'singleSelect') {
      return typeof colDef.valueOptions === 'function'
        ? colDef.valueOptions({ field: colDef.field })
        : colDef.valueOptions;
    }
  }, [columnsLookup, item.columnField]);

  const onFilterChange = React.useCallback(
    (event) => {
      const value = event.target.value;
      setFilterValueState(value);
      applyValue({ ...item, value });
    },
    [applyValue, item]
  );

  React.useEffect(() => {
    setFilterValueState(item.value || '');
  }, [item.value]);

  return (
    <ControllerSelect
      id={id}
      label={apiRef.current.getLocaleText('filterPanelInputLabel')}
      placeholder={apiRef.current.getLocaleText('filterPanelInputPlaceholder')}
      value={filterValueState}
      onChange={onFilterChange}
      SelectProps={{ native: true }}
      {...others}
    >
      {valueOptions?.map((option) =>
        typeof option === 'string' || typeof option === 'number' ? (
          <option key={option} value={option}>
            {option}
          </option>
        ) : (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        )
      )}
    </ControllerSelect>
  );
}

export function ControllerGridFilterInputAutocomplete(
  props: GridTypeFilterInputValueProps & ControllerSelectProps
) {
  const { item, applyValue, apiRef: _apiRef, type, children, ...others } = props;
  const apiRef = _apiRef as React.MutableRefObject<GridApiPro>;
  const columnsLookup = useGridSelector(apiRef, gridColumnLookupSelector);
  const [inputValue, setInputValue] = React.useState(item.value || '');
  const id = useId();

  const options = React.useMemo(() => {
    const colDef = columnsLookup[item.columnField];

    if (colDef?.type === 'singleSelect') {
      const valueOptions =
        typeof colDef.valueOptions === 'function'
          ? colDef.valueOptions({ field: colDef.field })
          : colDef.valueOptions;

      return (valueOptions || []).map((option) => {
        return typeof option === 'string' || typeof option === 'number'
          ? { label: String(option), value: String(option) }
          : option;
      });
    }
  }, [columnsLookup, item.columnField]);

  const selectedOption = React.useMemo(() => {
    return options?.find((option) => {
      return option.value === item.value;
    });
  }, [item.value, options]);

  const onFilterChange = React.useCallback(
    (event: unknown, option: string | AutocompleteOption | null) => {
      applyValue({ ...item, value: (typeof option === 'string' ? option : option?.value) || null });
    },
    [applyValue, item]
  );

  const onInputChange = (event: unknown, value: string) => {
    setInputValue(value);
  };

  return (
    <ControllerAutocomplete
      AutocompleteProps={{
        id,
        options: options || [],
        value: selectedOption || null,
        onChange: onFilterChange,
        inputValue,
        onInputChange,
      }}
      label={apiRef.current.getLocaleText('filterPanelInputLabel')}
      placeholder={apiRef.current.getLocaleText('filterPanelInputPlaceholder')}
      {...others}
    />
  );
}
